// site.config.json as the browser uses it (with a test build's preview flags, src/site/preview.ts), and the two
// switches of the launch: the ads and the sale of Remove Ads each start at `launch.at` and only if switched on. The
// device's clock decides here, for what the pages show; the functions decide the sale by the server's clock.
import siteConfigFile from '../../site.config.json';
import { previewConfig } from './preview';

/** A Plugin Boutique banner, exactly as the affiliate panel supplies it (null until copied from it). */
export interface AffiliateCreative {
  id: string;
  width: number;
  height: number;
  image: string | null;
  href: string | null;
  alt: { ko: string; en: string };
}

type SiteConfigFile = typeof siteConfigFile;
/** site.config.json with its open fields typed as such (the JSON gives `null` its own type). */
export type BrowserSiteConfig = Omit<SiteConfigFile, 'ads'> & {
  ads: Omit<SiteConfigFile['ads'], 'affiliate'> & { affiliate?: { creatives: AffiliateCreative[] } };
};

export const siteConfig: BrowserSiteConfig = previewConfig(siteConfigFile as BrowserSiteConfig, import.meta.env);

/** The launch (A), in milliseconds. */
export const launchAt: number = Date.parse(siteConfig.launch.at);

/** site.config.json product.removeAds.offered: the sale is switched on (it still starts at the launch). */
export const removeAdsOffered: boolean = siteConfig.product.removeAds.offered;

/** The build pays with Toss's test keys (VITE_TOSS_CLIENT_KEY test_…): its payments charge nothing. */
export const testPayments: boolean = (import.meta.env.VITE_TOSS_CLIENT_KEY ?? '').trim().startsWith('test_');

/**
 * Remove Ads can be bought now: switched on, and the launch has come, or before it on test keys (nothing is charged;
 * Toss Payments' review tests the checkout). The functions take only test payments before the launch.
 */
export function salesOpen(now: number = Date.now(), test: boolean = testPayments): boolean {
  return removeAdsOffered && (now >= launchAt || test);
}

/** The launch hasn't come yet (the pages then say when it comes). */
export function beforeLaunch(now: number = Date.now()): boolean {
  return now < launchAt;
}

/** Pages that allow ads may show one now: switched on (they then have a slot), and the launch has come. */
export function adsRunning(now: number = Date.now()): boolean {
  return siteConfig.ads.enabled && now >= launchAt;
}
